import {
    AssumptionDefaultDto,
    DEFAULT_SOURCE,
    PrefilledVariantField,
    StampedField,
    SteckbriefAssumptionDefaultsDto,
    USER_PROVIDED_SOURCE,
    VariantDataStamp,
} from './ensan-steckbrief.dto';

/*
 * Model defaults per Gebäudetyp on the Variant form (GEN-802, ADR-023). The
 * VALUES always come from `GET /v1/ensan/steckbriefe/assumption-defaults` —
 * this module only knows WHICH form fields are defaulted and applies the
 * backend's rules on the client: seed an empty field, follow a Gebäudetyp pick
 * while the value is still the previous default, and show the source a save
 * will stamp. Pure helpers importing only the DTO module, so the fields module
 * can use them without an import cycle.
 */

/**
 * The form fields seeded from the assumption defaults and following the
 * Gebäudetyp — each a NUMBER field of the model. Adding the next assumption
 * means one backend catalog field plus one entry here.
 */
export const ASSUMPTION_DEFAULTED_FIELD_KEYS = ['windowAreaSharePercent'] as const;

export type AssumptionDefaultedFieldKey = (typeof ASSUMPTION_DEFAULTED_FIELD_KEYS)[number];

export function isAssumptionDefaultedField(key: string): key is AssumptionDefaultedFieldKey {
    return (ASSUMPTION_DEFAULTED_FIELD_KEYS as readonly string[]).includes(key);
}

/**
 * The window area share the form seeded before GEN-802 (GEN-774) — twin of the
 * backend `RETIRED_WINDOW_AREA_SHARE_SEED_PERCENT`. A legacy stamp carries it
 * as USER_PROVIDED although nobody entered it, so it is never carried (see
 * [stampPrefillFields]).
 */
export const RETIRED_WINDOW_AREA_SHARE_SEED_PERCENT = 35;

/** Retired form seed per defaulted field — the legacy rule of [stampPrefillFields]. */
const RETIRED_FORM_SEEDS: Record<AssumptionDefaultedFieldKey, number> = {
    windowAreaSharePercent: RETIRED_WINDOW_AREA_SHARE_SEED_PERCENT,
};

/**
 * The default of `key` for a Gebäudetyp: the code's row when the configuration
 * lists it, else the base row (a null, unlisted or unknown code). `Object.hasOwn`
 * keeps codes like `__proto__` or `toString` off the prototype chain. Null
 * without a configuration (still loading or failed) or without the key.
 */
export function resolveAssumptionDefault(
    defaults: SteckbriefAssumptionDefaultsDto | null,
    key: string,
    buildingFunction: unknown,
): AssumptionDefaultDto | null {
    if (defaults == null) return null;
    const row =
        typeof buildingFunction === 'string' && Object.hasOwn(defaults.byBuildingFunction, buildingFunction)
            ? defaults.byBuildingFunction[buildingFunction]
            : defaults.base;
    return Object.hasOwn(row, key) ? row[key] : null;
}

/** An empty form value: null, undefined or a cleared input's empty string. */
function isEmptyFormValue(value: unknown): boolean {
    return value == null || value === '';
}

/**
 * Whether a form value IS the default — the frontend twin of the backend
 * `ConfiguredValue.matches`: a number equal to the default value. A numeric
 * string never matches (the backend stamps it USER_PROVIDED).
 */
export function isAssumptionDefaultValue(value: unknown, assumptionDefault: AssumptionDefaultDto | null): boolean {
    return assumptionDefault !== null && typeof value === 'number' && value === assumptionDefault.value;
}

/**
 * The seed patch of the form's initial values: every EMPTY defaulted field
 * gets the default of `values.buildingFunction`. A carried or stamped value
 * wins. Empty without a configuration — the backend then applies its default.
 */
export function seedAssumptionDefaults(
    defaults: SteckbriefAssumptionDefaultsDto | null,
    values: Record<string, unknown>,
): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    for (const key of ASSUMPTION_DEFAULTED_FIELD_KEYS) {
        if (!isEmptyFormValue(values[key])) continue;
        const assumptionDefault = resolveAssumptionDefault(defaults, key, values['buildingFunction']);
        if (assumptionDefault !== null) patch[key] = assumptionDefault.value;
    }
    return patch;
}

/**
 * The follow patch after the Gebäudetyp moved from `previousCode` to
 * `nextCode` (a pick or a prefill merge): a defaulted field that is empty or
 * still the previous code's default takes the next code's default; a value the
 * advisor edited stays. Empty without a configuration.
 */
export function followAssumptionDefaults(
    defaults: SteckbriefAssumptionDefaultsDto | null,
    values: Record<string, unknown>,
    previousCode: unknown,
    nextCode: unknown,
): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    for (const key of ASSUMPTION_DEFAULTED_FIELD_KEYS) {
        const current = values[key];
        const untouched =
            isEmptyFormValue(current) ||
            isAssumptionDefaultValue(current, resolveAssumptionDefault(defaults, key, previousCode));
        const next = resolveAssumptionDefault(defaults, key, nextCode);
        if (untouched && next !== null) patch[key] = next.value;
    }
    return patch;
}

/**
 * The source badge of a defaulted field — exactly what a save will stamp
 * (backend `VariantStampBuilder`): empty → null, the current Gebäudetyp's
 * default → `DEFAULT`, anything else → `USER_PROVIDED`. Undefined
 * for a field that is not defaulted or without a configuration: the caller's
 * generic prefill-baseline rule applies then.
 */
export function assumptionDefaultSource(
    defaults: SteckbriefAssumptionDefaultsDto | null,
    key: string,
    value: unknown,
    buildingFunction: unknown,
): string | null | undefined {
    if (!isAssumptionDefaultedField(key)) return undefined;
    const assumptionDefault = resolveAssumptionDefault(defaults, key, buildingFunction);
    if (assumptionDefault === null) return undefined;
    if (isEmptyFormValue(value)) return null;
    return isAssumptionDefaultValue(value, assumptionDefault) ? DEFAULT_SOURCE : USER_PROVIDED_SOURCE;
}

/**
 * The open-mode prefill fields of a stamp (GEN-802 legacy rule, twin of the
 * backend `VariantDataStamp.carriedField`): the stamp's fields, except that a
 * stamp saved before GEN-802 (no `assumptionDefaults` section) drops a
 * USER_PROVIDED value equal to its field's retired form seed — the old form
 * seeded it, nobody entered it. The field then re-seeds from the current
 * defaults. Generation keeps the stamp verbatim.
 */
export function stampPrefillFields(stamp: VariantDataStamp): Record<string, StampedField> {
    if (stamp.assumptionDefaults != null) return stamp.fields;
    const fields = { ...stamp.fields };
    for (const key of ASSUMPTION_DEFAULTED_FIELD_KEYS) {
        const entry = fields[key];
        if (entry?.source === USER_PROVIDED_SOURCE && entry.value === RETIRED_FORM_SEEDS[key]) {
            fields[key] = { value: null, source: null };
        }
    }
    return fields;
}

/**
 * The prefill fields without `DEFAULT`-sourced values (GEN-802): a default is
 * never carried — the form re-seeds it from the current configuration for the
 * current Gebäudetyp. That covers GEN-802 stamps and GEN-801-era ones (the
 * fixed 35 %), whose value would otherwise be re-saved as USER_PROVIDED and
 * carried from then on. Only an open-mode prefill can carry `DEFAULT` (a
 * server prefill carries USER_PROVIDED values only).
 */
export function dropAssumptionDefaults(
    fields: Record<string, PrefilledVariantField>,
): Record<string, PrefilledVariantField> {
    if (!Object.values(fields).some((entry) => entry?.source === DEFAULT_SOURCE)) return fields;
    return Object.fromEntries(
        Object.entries(fields).map(([key, entry]) => [
            key,
            entry?.source === DEFAULT_SOURCE ? { value: null, source: null } : entry,
        ]),
    );
}
